import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'

import type { SLAConfig } from '@/lib/api/sla-config-api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    useSLAConfigs,
    useSetSLAConfigActive,
    useUpsertSLAConfig,
} from '@/hooks/use-sla-config-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/_app/_authed/_sidebar/settings/sla')({
    component: SLASettingsPage,
})

interface EditRow {
    status: string
    duration_minutes: number
    warning_minutes: number | ''
}

function SLASettingsPage() {
    const { data: configs = [], isLoading } = useSLAConfigs()
    const upsertMutation = useUpsertSLAConfig()
    const activeMutation = useSetSLAConfigActive()

    const [editing, setEditing] = useState<EditRow | null>(null)
    const [editingId, setEditingId] = useState<string | null>(null)

    const startEdit = (cfg: SLAConfig) => {
        setEditingId(cfg.id)
        setEditing({
            status: cfg.status,
            duration_minutes: cfg.duration_minutes,
            warning_minutes: cfg.warning_minutes ?? '',
        })
    }

    const handleSave = async () => {
        if (!editing) return
        await upsertMutation.mutateAsync({
            status: editing.status,
            duration_minutes: editing.duration_minutes,
            warning_minutes:
                editing.warning_minutes !== ''
                    ? editing.warning_minutes
                    : undefined,
        })
        setEditing(null)
        setEditingId(null)
    }

    if (isLoading) {
        return (
            <div className="text-sm text-muted-foreground">
                {m.common_loading()}
            </div>
        )
    }

    return (
        <div className="max-w-3xl space-y-6">
            <div>
                <h1 className="text-xl font-semibold">
                    {m.sla_settings_title()}
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    {m.sla_settings_description()}
                </p>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>{m.sla_settings_title()}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{m.sla_status()}</TableHead>
                                <TableHead>{m.sla_duration()}</TableHead>
                                <TableHead>{m.sla_warning()}</TableHead>
                                <TableHead>{m.sla_active()}</TableHead>
                                <TableHead>{m.common_actions()}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {configs.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={5}
                                        className="text-center text-muted-foreground py-6"
                                    >
                                        {m.common_no_data()}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                configs.map((cfg) => (
                                    <TableRow key={cfg.id}>
                                        <TableCell className="font-medium">
                                            {cfg.status}
                                        </TableCell>
                                        <TableCell>
                                            {editingId === cfg.id && editing ? (
                                                <Input
                                                    type="number"
                                                    min={1}
                                                    className="w-24"
                                                    value={
                                                        editing.duration_minutes
                                                    }
                                                    onChange={(e) =>
                                                        setEditing((prev) =>
                                                            prev
                                                                ? {
                                                                      ...prev,
                                                                      duration_minutes:
                                                                          Number(
                                                                              e
                                                                                  .target
                                                                                  .value,
                                                                          ),
                                                                  }
                                                                : prev,
                                                        )
                                                    }
                                                />
                                            ) : (
                                                cfg.duration_minutes
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {editingId === cfg.id && editing ? (
                                                <Input
                                                    type="number"
                                                    min={1}
                                                    className="w-24"
                                                    value={
                                                        editing.warning_minutes
                                                    }
                                                    placeholder="—"
                                                    onChange={(e) =>
                                                        setEditing((prev) =>
                                                            prev
                                                                ? {
                                                                      ...prev,
                                                                      warning_minutes:
                                                                          e
                                                                              .target
                                                                              .value
                                                                              ? Number(
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                                )
                                                                              : '',
                                                                  }
                                                                : prev,
                                                        )
                                                    }
                                                />
                                            ) : (
                                                (cfg.warning_minutes ?? '—')
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    activeMutation.mutate({
                                                        id: cfg.id,
                                                        is_active:
                                                            !cfg.is_active,
                                                    })
                                                }
                                                className={`text-xs font-medium px-2 py-0.5 rounded-full border transition-colors ${
                                                    cfg.is_active
                                                        ? 'border-green-500 text-green-600 bg-green-50'
                                                        : 'border-muted text-muted-foreground bg-muted/30'
                                                }`}
                                            >
                                                {cfg.is_active
                                                    ? m.sla_active()
                                                    : '—'}
                                            </button>
                                        </TableCell>
                                        <TableCell>
                                            {editingId === cfg.id ? (
                                                <div className="flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        disabled={
                                                            upsertMutation.isPending
                                                        }
                                                        onClick={handleSave}
                                                    >
                                                        {m.common_save()}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => {
                                                            setEditing(null)
                                                            setEditingId(null)
                                                        }}
                                                    >
                                                        {m.common_cancel()}
                                                    </Button>
                                                </div>
                                            ) : (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() =>
                                                        startEdit(cfg)
                                                    }
                                                >
                                                    {m.common_edit()}
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    )
}
